import type { Template, TemplateCampaign } from '../types'

/**
 * True for campaigns whose templates are revealed one-per-day (drip behavior).
 * Drives both the drip-day filtering and the "Day X" badge UI.
 */
export const isDripCampaign = (
  campaign: Pick<TemplateCampaign, 'campaignType'>,
): boolean => {
  return (
    campaign.campaignType === 'Drip Campaign' ||
    campaign.campaignType === 'Creator Camp'
  )
}

/**
 * Checks if a template belongs to a campaign via linkedCampaigns.
 */
export const isTemplateInCampaign = (
  template: Partial<Pick<Template, 'linkedCampaigns'>>,
  campaign: TemplateCampaign,
): boolean => {
  return template.linkedCampaigns?.includes(campaign.id) ?? false
}

/**
 * Gets the day number for a template within a campaign based on its position
 * in the campaign's templateIds array (1-indexed).
 * Returns undefined if the template is not in the campaign's templateIds.
 */
export const getTemplateDayInCampaign = (
  template: Template,
  campaign: Pick<TemplateCampaign, 'templateIds'>,
): number | undefined => {
  if (!campaign.templateIds?.length) {
    return undefined
  }
  const index = campaign.templateIds.indexOf(template.id)
  return index >= 0 ? index + 1 : undefined
}

/**
 * Gets the current day number for a campaign, capped at the number of templates.
 * For drip campaigns, the day cannot exceed the total number of templates.
 */
export const getCappedCurrentDayNumber = (
  campaign: Pick<TemplateCampaign, 'currentDayNumber' | 'templateIds'>,
): number => {
  const currentDay = campaign.currentDayNumber ?? 1
  const maxDay = campaign.templateIds?.length ?? currentDay
  return Math.min(currentDay, maxDay)
}

/**
 * Picks which template the banner should feature today.
 *
 * - Drip campaigns: the template scheduled for the current day, capped at
 *   the last template once the drip exhausts.
 * - Collections: rotates through the available templates one per day so the
 *   banner doesn't get stuck on a single prompt for the entire run.
 */
export const pickCampaignBannerTemplate = (
  campaign: Pick<TemplateCampaign, 'campaignType' | 'currentDayNumber'>,
  templates: Template[],
): Template | undefined => {
  if (templates.length === 0) return undefined

  const dayNumber = campaign.currentDayNumber ?? 1
  const zeroIndexedDay = Math.max(0, dayNumber - 1)

  if (isDripCampaign(campaign)) {
    return templates[Math.min(zeroIndexedDay, templates.length - 1)]
  }

  // Collection: rotate so each day surfaces a different template.
  return templates[zeroIndexedDay % templates.length]
}

/**
 * Checks if a template should be visible based on drip campaign day logic.
 * For drip campaigns, templates are only visible if their day number <= the
 * campaign's currentDayNumber. For Collections, all templates are always
 * visible.
 *
 * @returns true if the template should be visible, false if it should be hidden
 */
export const isTemplateVisibleInCampaign = (
  template: Template,
  campaign: TemplateCampaign,
): boolean => {
  // Collections show all templates regardless of day
  if (!isDripCampaign(campaign)) {
    return true
  }

  // For drip campaigns, check if the template's day has arrived
  const templateDay = getTemplateDayInCampaign(template, campaign)

  // If template is not in templateIds, hide it (data inconsistency)
  if (templateDay === undefined) {
    return false
  }

  // Only show templates whose day has arrived
  const currentDay = campaign.currentDayNumber ?? 0
  return templateDay <= currentDay
}

/**
 * Gets the campaign tags to display for a template.
 * Shows ALL campaigns the template belongs to, in priority order.
 * For drip campaigns, adds "Day X" as the last tag based on the template's
 * position in the campaign.
 *
 * @returns Array of campaign tag strings (empty array if no tags to show)
 */
export const getCampaignTagsForTemplate = ({
  activeCampaigns,
  template,
}: {
  template: Template
  activeCampaigns: TemplateCampaign[]
}): string[] => {
  if (!template || activeCampaigns.length === 0) {
    return []
  }

  // Find campaigns this template belongs to (already in priority order from activeCampaigns)
  const templateCampaigns = activeCampaigns.filter((campaign) =>
    isTemplateInCampaign(template, campaign),
  )

  const tags: string[] = []

  for (const campaign of templateCampaigns) {
    tags.push(campaign.title ?? campaign.name)

    if (isDripCampaign(campaign)) {
      const dayNumber = getTemplateDayInCampaign(template, campaign)
      if (dayNumber !== undefined) {
        tags.push(`Day ${dayNumber}`)
      }
    }
  }

  return tags
}
